// 📄 hitoService.ts
// Hitos y avance del proyecto (plan-proyectos-generales.md, G4): parte de
// "Gestión del proyecto", así que existe en TODO proyecto (también SAP,
// donde convive con su checklist de Ariba). Las tareas se pueden asociar a
// un hito (tareaService.ts, `hitoId`).
//
// El avance general (hitos completados / total) se guarda resumido en el
// propio proyecto (`avanceHitos`) para que Mis Proyectos y la portada lo
// muestren sin leer la subcolección de cada proyecto. Se recalcula después
// de cada cambio (recalcularAvance).
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  writeBatch,
  serverTimestamp
} from "firebase/firestore";
import { db } from '../firebase';
import { ProjectMember } from './projectService.ts';

export type HitoEstado = 'Pendiente' | 'En curso' | 'Completado';
export const ESTADOS_HITO: HitoEstado[] = ['Pendiente', 'En curso', 'Completado'];

export interface HitoFormData {
  nombre: string;
  descripcion: string;
  fechaObjetivo: string; // YYYY-MM-DD, opcional ("" = sin fecha)
  responsableUid: string; // "" = sin responsable
}

export interface Hito extends HitoFormData {
  id: string;
  estado: HitoEstado;
  // Orden de la lista (los de plantilla vienen en orden; los nuevos van al final).
  orden: number;
  responsableNombre: string; // email denormalizado, mismo criterio que asignadoANombre en tareas
  creadoPor: string;
  creadoEn: any;
}

export interface AvanceHitos {
  completados: number;
  total: number;
}

const hitosRef = (projectId: string) => collection(db, "projects", projectId, "hitos");
const hitoRef = (projectId: string, hitoId: string) => doc(db, "projects", projectId, "hitos", hitoId);

export const porcentajeAvance = (avance?: AvanceHitos | null): number =>
  avance && avance.total > 0 ? Math.round((avance.completados / avance.total) * 100) : 0;

export const getHitos = async (projectId: string): Promise<Hito[]> => {
  const snap = await getDocs(hitosRef(projectId));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<Hito, "id">) }))
    .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
};

/**
 * Recalcula el resumen del proyecto a partir de la subcolección. Best-effort:
 * un owner/admin de la org que no es miembro puede editar hitos pero no el
 * documento del proyecto (reglas); el hito ya se guardó y el resumen se
 * corrige en el próximo cambio de un miembro.
 */
export const recalcularAvance = async (projectId: string): Promise<AvanceHitos> => {
  const hitos = await getHitos(projectId);
  const avance = { completados: hitos.filter((h) => h.estado === 'Completado').length, total: hitos.length };
  await updateDoc(doc(db, "projects", projectId), { avanceHitos: avance })
    .catch((err) => console.error('No se pudo actualizar el avance del proyecto:', err));
  return avance;
};

const responsable = (miembros: ProjectMember[], uid: string) => {
  if (!uid) return '';
  const m = miembros.find((x) => x.userId === uid);
  if (!m) throw new Error("El responsable ya no es miembro de este proyecto.");
  return m.email;
};

export const createHito = async (projectId: string, data: HitoFormData, miembros: ProjectMember[], creadoPor: string): Promise<string> => {
  if (!data.nombre.trim()) throw new Error("El nombre del hito es obligatorio.");
  const ref = doc(hitosRef(projectId));
  await setDoc(ref, {
    nombre: data.nombre.trim(),
    descripcion: data.descripcion.trim(),
    fechaObjetivo: data.fechaObjetivo,
    responsableUid: data.responsableUid,
    responsableNombre: responsable(miembros, data.responsableUid),
    estado: 'Pendiente' as HitoEstado,
    orden: Date.now(),
    creadoPor,
    creadoEn: serverTimestamp()
  });
  await recalcularAvance(projectId);
  return ref.id;
};

export const updateHitoDatos = async (projectId: string, hitoId: string, data: HitoFormData, miembros: ProjectMember[]): Promise<void> => {
  if (!data.nombre.trim()) throw new Error("El nombre del hito es obligatorio.");
  await updateDoc(hitoRef(projectId, hitoId), {
    nombre: data.nombre.trim(),
    descripcion: data.descripcion.trim(),
    fechaObjetivo: data.fechaObjetivo,
    responsableUid: data.responsableUid,
    responsableNombre: responsable(miembros, data.responsableUid)
  });
};

export const updateHitoEstado = async (projectId: string, hitoId: string, estado: HitoEstado): Promise<void> => {
  await updateDoc(hitoRef(projectId, hitoId), { estado });
  await recalcularAvance(projectId);
};

/** Las tareas asociadas no se borran: quedan sin hito (ver TareasSection). */
export const deleteHito = async (projectId: string, hitoId: string): Promise<void> => {
  await deleteDoc(hitoRef(projectId, hitoId));
  await recalcularAvance(projectId);
};

/** Hitos sugeridos por la plantilla al crear el proyecto (ver PLANTILLAS en modulosConfig.ts). */
export const crearHitosDePlantilla = async (projectId: string, nombres: string[], creadoPor: string): Promise<void> => {
  if (!nombres.length) return;
  const batch = writeBatch(db);
  const base = Date.now();
  nombres.forEach((nombre, i) => {
    batch.set(doc(hitosRef(projectId)), {
      nombre,
      descripcion: '',
      fechaObjetivo: '',
      responsableUid: '',
      responsableNombre: '',
      estado: 'Pendiente' as HitoEstado,
      orden: base + i,
      creadoPor,
      creadoEn: serverTimestamp()
    });
  });
  batch.update(doc(db, "projects", projectId), { avanceHitos: { completados: 0, total: nombres.length } });
  await batch.commit();
};

/**
 * Cuando una tarea del hito empieza o se completa, el hito pasa solo de
 * Pendiente a En curso. Nunca a Completado: eso lo decide el equipo
 * (HitosSection.tsx sugiere completarlo cuando todas sus tareas están
 * listas). Best-effort: el rol cliente puede mover su tarea pero no el hito.
 */
export const iniciarHitoSiPendiente = async (projectId: string, hitoId: string): Promise<boolean> => {
  try {
    const snap = await getDoc(hitoRef(projectId, hitoId));
    if (!snap.exists() || snap.data().estado !== 'Pendiente') return false;
    await updateDoc(hitoRef(projectId, hitoId), { estado: 'En curso' as HitoEstado });
    return true;
  } catch (err) {
    console.error('No se pudo pasar el hito a En curso:', err);
    return false;
  }
};

// Fecha LOCAL (no UTC): de noche en América, toISOString ya da el día siguiente.
const hoyStr = (): string => new Date().toLocaleDateString('en-CA');
export const hitoAtrasado = (h: Hito): boolean => h.estado !== 'Completado' && !!h.fechaObjetivo && h.fechaObjetivo < hoyStr();
